import { FC, lazy, Suspense } from 'react';
import { Route, Switch } from 'react-router-dom';
import { Container } from 'src/components/container';
import { CookieBanner } from 'src/components/cookie-banner';
import { CookieCheck } from 'src/components/cookie-check';
import { ErrorBoundary } from 'src/components/error-boundary';
import { FatalErrorBoundary } from 'src/components/fatal-error-boundary';
import { Footer } from 'src/components/footer';
import { Header } from 'src/components/header';
import {
  Layout,
  LayoutFooter,
  LayoutHeader,
  LayoutMain,
} from 'src/components/layout';
import {
  ScrollManagerProvider,
  stickyHeaderId,
} from 'src/components/scroll-management';
import { ScrollToTop } from 'src/components/scroll-to-top';
import { SimplePageLayout } from 'src/components/simple-page-layout';
import { PageSpinner } from 'src/components/spinner-container';
import { GlobalResourcesProvider } from 'src/global-resources-provider';
import { AxiosHookConfigProvider } from 'src/hooks/use-axios';
import { BreakpointsProvider } from 'src/hooks/use-breakpoints';
import { DropManagerProvider } from 'src/hooks/use-drop-manager';
import { FullwidthProvider } from 'src/hooks/use-fullwidth';
import {
  HeaderHeightProvider,
  useHeaderHeightMeasure,
} from 'src/hooks/use-header-height';
import { useInitialCleanup } from 'src/hooks/use-initial-cleanup';
import { useOurProfiler } from 'src/hooks/use-our-profiler';
import { PreviousLocationProvider } from 'src/hooks/use-previous-location';
import { useWithinStoryPage } from 'src/hooks/use-within-story-page';
import { useDeveloperTools } from 'src/pages/developer-tools/use-developer-tools';
import { Platform } from 'src/pages/platform';
import { useIsSnamAdmin } from 'src/pages/snam-bid-collector/use-is-snam-admin';
import { Routes } from 'src/routes';
import { useCustomAxiosConfig } from 'src/utils/axios-config';
import styled from 'styled-components';

const FixedBottom = styled.div`
  position: fixed;
  bottom: 0;
  width: 100%;
`;

const DataTestidHighligher = lazy(
  () => import('src/pages/developer-tools/data-testid-highlighter')
);

const EnhancedHeader = () => {
  const ref = useHeaderHeightMeasure();
  const isSnamAdmin = useIsSnamAdmin();

  return (
    <LayoutHeader id={stickyHeaderId} ref={ref}>
      <Header isSnamAdmin={isSnamAdmin} />
    </LayoutHeader>
  );
};

export const App: FC = () => {
  useInitialCleanup();
  const withinStoryPage = useWithinStoryPage();
  const axiosHookConfig = useCustomAxiosConfig();
  useOurProfiler('App');
  const developerTools = useDeveloperTools();

  return (
    <AxiosHookConfigProvider value={axiosHookConfig}>
      <PreviousLocationProvider>
        <DropManagerProvider>
          <BreakpointsProvider>
            <HeaderHeightProvider>
              <FullwidthProvider>
                <Suspense
                  fallback={
                    <SimplePageLayout>
                      <PageSpinner />
                    </SimplePageLayout>
                  }
                >
                  <FatalErrorBoundary>
                    <ScrollToTop />

                    <CookieCheck />

                    <GlobalResourcesProvider>
                      <Layout>
                        <EnhancedHeader />

                        <LayoutMain>
                          {withinStoryPage ? null : <Platform />}
                          <Switch>
                            <Route path="/platform">
                              {withinStoryPage ? (
                                <p>
                                  This is an Angular page. This page is{' '}
                                  <strong>not</strong> documentend in Storybook.
                                </p>
                              ) : null}
                            </Route>

                            <Route>
                              <Container>
                                <ScrollManagerProvider>
                                  <Suspense fallback={<PageSpinner />}>
                                    <ErrorBoundary>
                                      <Routes />
                                    </ErrorBoundary>
                                  </Suspense>
                                </ScrollManagerProvider>
                              </Container>
                            </Route>
                          </Switch>
                        </LayoutMain>

                        <LayoutFooter>
                          <Footer />
                        </LayoutFooter>
                      </Layout>

                      {!withinStoryPage && (
                        <FixedBottom>
                          <CookieBanner />
                        </FixedBottom>
                      )}

                      {developerTools && (
                        <Suspense fallback={null}>
                          <DataTestidHighligher />
                        </Suspense>
                      )}
                    </GlobalResourcesProvider>
                  </FatalErrorBoundary>
                </Suspense>
              </FullwidthProvider>
            </HeaderHeightProvider>
          </BreakpointsProvider>
        </DropManagerProvider>
      </PreviousLocationProvider>
    </AxiosHookConfigProvider>
  );
};
